import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dbHealth, setDbHealth] = useState(null);

  useEffect(() => {
    api.get("/db/health").then((r) => setDbHealth(r.data)).catch(() => setDbHealth({ connected: false }));
  }, []);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Live DB connection badge — top left */}
      <div className="absolute top-3 left-3 z-20" data-testid="db-health-badge">
        {dbHealth && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur ${
            dbHealth.connected
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
              : "bg-red-500/15 text-red-300 border-red-400/30"
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${dbHealth.connected ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
            {dbHealth.connected
              ? `Live DB · ${dbHealth.db}${dbHealth.facilities ? ` · ${dbHealth.facilities} facilities` : ""}`
              : "DB disconnected"}
          </span>
        )}
      </div>
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#111111] relative flex-col justify-between p-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1599676069558-30669a7a4f4a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#111111] via-[#111111]/85 to-[#8c0d20]/40" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-sm px-4 py-2.5 inline-flex shadow-lg">
            <img src="/redfort-logo.gif" alt="Red Fort Capital" className="h-9 w-auto object-contain" />
          </div>
        </div>
        <div className="relative z-10">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#ff8fa0] mb-4" data-testid="login-product-name">Red Fort Capital Loan Pulse</div>
          <h1 className="font-display text-4xl font-extrabold text-white leading-tight">
            Loan Management<br />Intelligence
          </h1>
          <p className="text-slate-300 mt-4 max-w-md">
            Originate, service and monitor your loan book — deal size, interest rates, collections and borrower exposure in one place.
          </p>
        </div>
        <div className="relative z-10 text-slate-400 text-sm">© {new Date().getFullYear()} Red Fort Capital</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center bg-[#F4F1EA] p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="bg-white rounded-sm px-3 py-2 inline-flex border border-slate-200">
              <img src="/redfort-logo.gif" alt="Red Fort Capital" className="h-7 w-auto object-contain" />
            </div>
          </div>
          <h2 className="font-display text-3xl font-extrabold text-[#1a1a1b]">Sign in</h2>
          <p className="text-slate-500 mt-2 mb-8">Access Red Fort Capital Loan Pulse.</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full px-4 py-3 rounded-sm border border-slate-300 focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition"
                placeholder="you@redfort.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full px-4 py-3 rounded-sm border border-slate-300 focus:border-[#b3122a] focus:ring-2 focus:ring-[#b3122a]/20 outline-none transition"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <div className="text-sm text-[#b3122a] bg-[#b3122a]/10 rounded-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#b3122a] hover:bg-[#8c0d20] text-white font-semibold py-3 rounded-sm transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-5 text-[12px] text-slate-500 text-center">Borrower? <a href="/portal/signup" className="rf-link" data-testid="login-to-signup">Create your portal login with PAN + mobile OTP</a></div>
        </div>
      </div>
    </div>
  );
}
